import React, { useEffect, useState } from 'react';
import { studentService } from '@services/student/studentService';
import { PlayCircle, Video, FileVideo, Clock, Calendar, ArrowLeft, FileText, ExternalLink, CheckCircle, XCircle } from 'lucide-react';
import { useRipple } from '@/hooks/useRipple';
import { useStudentTheme } from '@context/StudentThemeContext';

interface PlaybackItem {
    name: string;
    url: string;
    isLocal: boolean;
}

const isSameDay = (d1: string | Date | undefined, d2: string | Date | undefined): boolean => {
    if (!d1 || !d2) return false;
    const date1 = new Date(d1);
    const date2 = new Date(d2);
    if (isNaN(date1.getTime()) || isNaN(date2.getTime())) return false;
    const iso1 = typeof d1 === 'string' && d1.includes('T') ? d1.split('T')[0] : date1.toISOString().split('T')[0];
    const iso2 = typeof d2 === 'string' && d2.includes('T') ? d2.split('T')[0] : date2.toISOString().split('T')[0];
    return iso1 === iso2 || (
        (date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getDate() === date2.getDate()) ||
        (date1.getUTCFullYear() === date2.getUTCFullYear() &&
         date1.getUTCMonth() === date2.getUTCMonth() &&
         date1.getUTCDate() === date2.getUTCDate())
    );
};

const renderAttendanceBadge = (status: string | null | undefined, isSelected = false) => {
    const normalizedStatus = status?.toUpperCase();
    if (normalizedStatus === 'PRESENT') {
        return (
            <span className={`inline-flex items-center gap-1 text-[9px] font-bold px-2 py-0.5 rounded-full shrink-0 border uppercase tracking-wider ${
                isSelected 
                    ? 'bg-emerald-500/20 text-emerald-200 border-emerald-400/30' 
                    : 'bg-emerald-50 text-emerald-700 border-emerald-200'
            }`}>
                <CheckCircle size={10} />
                Present
            </span>
        );
    }
    if (normalizedStatus === 'ABSENT') {
        return (
            <span className={`inline-flex items-center gap-1 text-[9px] font-bold px-2 py-0.5 rounded-full shrink-0 border uppercase tracking-wider ${
                isSelected 
                    ? 'bg-rose-500/20 text-rose-200 border-rose-400/30' 
                    : 'bg-rose-50 text-rose-700 border-rose-200'
            }`}>
                <XCircle size={10} />
                Absent
            </span>
        );
    }
    if (normalizedStatus === 'LATE') {
        return (
            <span className={`inline-flex items-center gap-1 text-[9px] font-bold px-2 py-0.5 rounded-full shrink-0 border uppercase tracking-wider ${
                isSelected 
                    ? 'bg-amber-500/20 text-amber-200 border-amber-400/30' 
                    : 'bg-amber-50 text-amber-700 border-amber-200'
            }`}>
                <Clock size={10} />
                Late
            </span>
        );
    }
    return null;
};

const StudentRecordings: React.FC = () => {
    const [scheduleData, setScheduleData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [enrollment, setEnrollment] = useState<any>(null);
    const [activeTab, setActiveTab] = useState<'calendar' | 'recordings'>('calendar');
    const [selectedDay, setSelectedDay] = useState<any | null>(null);
    const [currentPlayback, setCurrentPlayback] = useState<PlaybackItem | null>(null);
    const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
    const ripple = useRipple();
    const { theme } = useStudentTheme();

    const studentLinkItem = (selectedDay && selectedDay.is_mentor_session)
        ? selectedDay.student_links?.find((sl: any) => sl.enrollment_id === enrollment?.id)
        : null;
    const resolvedLink = (selectedDay && selectedDay.is_mentor_session)
        ? (studentLinkItem?.link || '')
        : (selectedDay?.link || '');
    const hasLink = !!resolvedLink;
    const resolvedTime = (selectedDay && selectedDay.is_mentor_session)
        ? (studentLinkItem?.meeting_time || '')
        : (selectedDay?.class_time || '');
    const resolvedDescription = (selectedDay && selectedDay.is_mentor_session)
        ? (studentLinkItem?.description || selectedDay.description || '')
        : (selectedDay?.description || '');

    const getFileUrl = (path: string) => {
        if (!path) return '#';
        if (path.startsWith('http')) return path;
        const baseUrl = import.meta.env.VITE_API_BASE_URL?.replace('/api/v1', '') || 'http://localhost:5050';
        return `${baseUrl}/${path.startsWith('/') ? path.slice(1) : path}`;
    };

    const formatClassTime = (timeStr: string | null | undefined) => {
        if (!timeStr) return '';
        try {
            const [hoursStr, minutesStr] = timeStr.split(':');
            const hours = parseInt(hoursStr, 10);
            const minutes = parseInt(minutesStr, 10);
            if (isNaN(hours) || isNaN(minutes)) return timeStr;
            
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const formattedHours = hours % 12 || 12;
            const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
            return `${formattedHours}:${formattedMinutes} ${ampm}`;
        } catch {
            return timeStr;
        }
    };

    const getPlaybacks = (day: any): PlaybackItem[] => {
        if (!day) return [];
        const list: PlaybackItem[] = [];
        if (day.recorded_video) {
            list.push({ name: 'External Link Recording', url: day.recorded_video, isLocal: false });
        }
        if (day.recorded_video_path) {
            list.push({ name: day.recorded_video_name || 'Class Recording', url: getFileUrl(day.recorded_video_path), isLocal: true });
        }
        if (day.recordings && day.recordings.length > 0) {
            day.recordings.forEach((rec: any) => {
                list.push({ name: rec.name, url: getFileUrl(rec.path), isLocal: true });
            });
        }
        return list;
    };

    useEffect(() => {
        const fetchSchedule = async () => {
            try {
                setLoading(true);
                const [scheduleRes, dashboardRes] = await Promise.all([
                    studentService.getSchedule(),
                    studentService.getDashboard().catch(() => null),
                ]);
                const schedule = scheduleRes?.data?.data || [];
                setScheduleData(schedule);

                if (dashboardRes?.data?.data?.enrollment) {
                    setEnrollment(dashboardRes.data.data.enrollment);
                }

                // Flatten days and select the first one by default
                const allDays: any[] = [];
                schedule.forEach((week: any) => {
                    if (week.days) {
                        week.days.forEach((day: any) => {
                            if (day.is_active !== false) {
                                allDays.push({
                                    ...day,
                                    week_number: week.week_number,
                                    week_title: week.title,
                                });
                            }
                        });
                    }
                });
                
                if (allDays.length > 0) {
                    allDays.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
                    setSelectedDay(allDays[0]);
                }
            } catch (error) {
                console.error("Failed to fetch schedule for recordings", error);
            } finally {
                setLoading(false);
            }
        };

        fetchSchedule();
    }, []);

    // Effect to automatically select first playback when active selected session changes
    useEffect(() => {
        if (selectedDay) {
            const playbacks = getPlaybacks(selectedDay);
            if (playbacks.length > 0) {
                setCurrentPlayback(playbacks[0]);
            } else {
                setCurrentPlayback(null);
            }
        } else {
            setCurrentPlayback(null);
        }
    }, [selectedDay]);

    // Flatten and filter days based on active tab
    const getAllFilteredDays = () => {
        const allDays: any[] = [];
        scheduleData.forEach((week: any) => {
            if (week.days) {
                week.days.forEach((day: any) => {
                    if (day.is_active !== false) {
                        allDays.push({
                            ...day,
                            week_number: week.week_number,
                            week_title: week.title,
                        });
                    }
                });
            }
        });
        
        allDays.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        if (activeTab === 'recordings') {
            return allDays.filter((day: any) => 
                day.is_unlocked && (day.recorded_video || day.recorded_video_path || (day.recordings && day.recordings.length > 0))
            );
        }

        return allDays;
    };

    const filteredDays = getAllFilteredDays();

    // Toggle active tab and select the first day in the new list
    const handleTabChange = (tab: 'calendar' | 'recordings') => {
        setActiveTab(tab);
        const list = tab === 'recordings' 
            ? getAllFilteredDays().filter((day: any) => day.is_unlocked && (day.recorded_video || day.recorded_video_path || (day.recordings && day.recordings.length > 0)))
            : getAllFilteredDays();
        
        if (list.length > 0) {
            setSelectedDay(list[0]);
        } else {
            setSelectedDay(null);
        }
        setMobileDetailOpen(false);
    };

    const formatDateLabel = (dateStr: string) => {
        const dateObj = new Date(dateStr);
        const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
        const dayNum = dateObj.toLocaleDateString('en-US', { day: 'numeric' });
        const monthName = dateObj.toLocaleDateString('en-US', { month: 'short' });
        return { dayName, dateStr: `${dayNum} ${monthName}` };
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    const selectedDayPlaybacks = selectedDay ? getPlaybacks(selectedDay) : [];

    return (
        <div className="space-y-6 flex flex-col h-[calc(100vh-112px)] min-h-[480px]">
            {/* Custom Tab Toggles & Navigation Row */}
            <div className="flex border-b border-border pb-1 shrink-0 gap-4">
                <button
                    onClick={() => handleTabChange('calendar')}
                    className={`py-2 px-1 text-sm font-bold border-b-2 transition-colors cursor-pointer focus:outline-none ${
                        activeTab === 'calendar'
                            ? 'border-primary text-primary'
                            : 'border-transparent text-slate-body hover:text-navy'
                    }`}
                >
                    <span className="flex items-center gap-1.5">
                        <Calendar size={16} />
                        Calendar
                    </span>
                </button>
                <button
                    onClick={() => handleTabChange('recordings')}
                    className={`py-2 px-1 text-sm font-bold border-b-2 transition-colors cursor-pointer focus:outline-none ${
                        activeTab === 'recordings'
                            ? 'border-primary text-primary'
                            : 'border-transparent text-slate-body hover:text-navy'
                    }`}
                >
                    <span className="flex items-center gap-1.5">
                        <PlayCircle size={16} />
                        Recordings
                    </span>
                </button>
            </div>

            {/* Main Split-Pane Content Panel */}
            <div className="flex-1 flex gap-6 overflow-hidden min-h-0">
                {/* LEFT LIST PANEL */}
                <div className={`w-full lg:w-[350px] bg-white rounded-2xl border border-border shadow-soft overflow-y-auto flex flex-col p-4 shrink-0 ${mobileDetailOpen ? 'hidden lg:flex' : 'flex'}`}>
                    {filteredDays.length === 0 ? (
                        <div className="flex-1 flex flex-col items-center justify-center text-center p-6 gap-3">
                            <div className="p-3 bg-slate-50 text-slate-400 rounded-full border border-gray-100">
                                <FileVideo size={36} />
                            </div>
                            <div>
                                <h4 className="text-sm font-bold text-navy">No Sessions Found</h4>
                                <p className="text-xs text-slate-body mt-1 max-w-[200px]">There are no sessions matching this category right now.</p>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {(() => {
                                let lastDateKey = '';
                                return filteredDays.map((day: any) => {
                                    const { dayName, dateStr } = formatDateLabel(day.date);
                                    const dateKey = `${dayName}_${dateStr}`;
                                    const isNewDate = dateKey !== lastDateKey;
                                    if (isNewDate) {
                                        lastDateKey = dateKey;
                                    }

                                    const isSelected = selectedDay && selectedDay.week_number === day.week_number && selectedDay.day_number === day.day_number;
                                    const playbacks = getPlaybacks(day);
                                    const hasRec = playbacks.length > 0;
                                    const studentLink = day.is_mentor_session ? day.student_links?.find((sl: any) => sl.enrollment_id === enrollment?.id) : null;
                                    const resolvedDayTime = day.is_mentor_session ? (studentLink?.meeting_time || '') : (day.class_time || '');
                                    const attRecord = enrollment?.attendance ? enrollment.attendance.find((a: any) => isSameDay(a.date, day.date)) : null;
                                    const attendanceStatus = day.attendance_status || attRecord?.status || null;

                                    return (
                                        <div key={`week-${day.week_number}-day-${day.day_number}`} className="space-y-2">
                                            {isNewDate && (
                                                <div className="pt-2 first:pt-0">
                                                    <span className="text-[10px] uppercase tracking-wider font-extrabold text-slate-400 block mb-0.5">
                                                        {dayName}
                                                    </span>
                                                    <span className="text-xs font-black text-navy">
                                                        {dateStr}
                                                    </span>
                                                </div>
                                            )}

                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSelectedDay(day);
                                                    setMobileDetailOpen(true);
                                                }}
                                                onPointerDown={ripple.onPointerDown}
                                                className={`w-full text-left p-3.5 rounded-xl border transition-all cursor-pointer flex flex-col gap-2 relative overflow-hidden group ${
                                                    isSelected
                                                        ? theme === 'dark'
                                                            ? 'bg-primary border-primary text-white shadow-card'
                                                            : 'bg-slate-900 border-slate-900 text-white shadow-md'
                                                        : 'bg-white border-border text-slate-800 hover:bg-slate-50 hover:border-slate-300'
                                                }`}
                                            >
                                                {/* Left indicator bar */}
                                                <span className={`absolute left-0 top-0 bottom-0 w-1 transition-colors ${
                                                    isSelected ? 'bg-primary' : 'bg-transparent group-hover:bg-slate-200'
                                                }`} />

                                                <div className="flex flex-col gap-1.5 pl-1">
                                                    <div className="flex justify-between items-start gap-2">
                                                        <span className={`text-xs font-bold leading-snug line-clamp-2 ${isSelected ? 'text-white' : 'text-navy'}`}>
                                                            {day.title}
                                                        </span>
                                                        <div className="flex items-center gap-1 shrink-0">
                                                            {renderAttendanceBadge(attendanceStatus, isSelected)}
                                                            {hasRec ? (
                                                                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0 border uppercase tracking-wider ${
                                                                    isSelected 
                                                                        ? 'bg-purple-500/20 text-purple-200 border-purple-400/20' 
                                                                        : 'bg-purple-50 text-purple-700 border-purple-100'
                                                                }`}>
                                                                    Rec
                                                                </span>
                                                            ) : day.link ? (
                                                                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0 border uppercase tracking-wider ${
                                                                    isSelected 
                                                                        ? 'bg-blue-500/20 text-blue-200 border-blue-400/20' 
                                                                        : 'bg-blue-50 text-primary border-primary/10'
                                                                }`}>
                                                                    Live
                                                                </span>
                                                            ) : null}
                                                        </div>
                                                    </div>

                                                    {day.is_mentor_session && (
                                                        <span className={`text-[9px] font-extrabold px-1.5 py-0.5 rounded-full border uppercase tracking-wider w-fit ${
                                                            isSelected 
                                                                ? 'bg-indigo-500/30 text-indigo-200 border-indigo-400/20' 
                                                                : 'bg-indigo-50 text-indigo-700 border-indigo-100'
                                                        }`}>
                                                            Mentor Session
                                                        </span>
                                                    )}
                                                </div>

                                                {resolvedDayTime && (
                                                    <div className="flex items-center gap-3 text-[10px] pl-1 font-medium">
                                                        <span className="flex items-center gap-1 shrink-0">
                                                            <Clock size={11} />
                                                            {formatClassTime(resolvedDayTime)}
                                                        </span>
                                                    </div>
                                                )}
                                            </button>
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                    )}
                </div>

                {/* RIGHT DETAILS/PLAYER PANEL */}
                <div className={`flex-1 bg-white rounded-2xl border border-border shadow-soft overflow-y-auto p-6 ${mobileDetailOpen ? 'flex flex-col' : 'hidden lg:flex flex-col'}`}>
                    {!selectedDay ? (
                        <div className="flex-1 flex flex-col items-center justify-center text-center p-6 gap-3">
                            <div className="p-4 bg-slate-50 text-slate-400 rounded-full border border-gray-100">
                                <Video size={48} />
                            </div>
                            <div>
                                <h3 className="text-base font-bold text-navy">No Session Selected</h3>
                                <p className="text-xs text-slate-body mt-1 max-w-[280px]">Select a session from the list to view playbacks, live links, and attachments.</p>
                            </div>
                        </div>
                    ) : (() => {
                        const selectedAttRecord = enrollment?.attendance ? enrollment.attendance.find((a: any) => isSameDay(a.date, selectedDay.date)) : null;
                        const selectedAttStatus = selectedDay.attendance_status || selectedAttRecord?.status || null;

                        return (
                        <div className="space-y-6 flex-1 flex flex-col">
                            {/* Mobile back navigation bar */}
                            <button
                                type="button"
                                onClick={() => setMobileDetailOpen(false)}
                                className="lg:hidden flex items-center gap-1.5 text-xs font-bold text-primary hover:underline cursor-pointer mb-2 shrink-0 self-start"
                            >
                                <ArrowLeft size={14} />
                                Back to sessions list
                            </button>

                            {/* Session Header Details */}
                            <div className="border-b border-border pb-4 shrink-0">
                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                    {selectedDay.is_mentor_session && (
                                        <span className="text-[10px] font-extrabold text-indigo-700 px-2 py-0.5 bg-indigo-50 border border-indigo-100 rounded-full uppercase tracking-wider">
                                            Mentor Session
                                        </span>
                                    )}
                                    {renderAttendanceBadge(selectedAttStatus, false)}
                                </div>
                                <h2 className="text-xl font-bold text-navy leading-tight">
                                    {selectedDay.title}
                                </h2>
                                <div className="flex flex-wrap gap-4 mt-3 text-xs text-slate-body font-medium">
                                    <span className="flex items-center gap-1">
                                        <Calendar size={13} className="text-slate-400" />
                                        {new Date(selectedDay.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                                    </span>
                                    {resolvedTime && (
                                        <span className="flex items-center gap-1">
                                            <Clock size={13} className="text-slate-400" />
                                            {formatClassTime(resolvedTime)}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Session Media / Player Pane (Plays Directly Inline!) */}
                            <div className="space-y-4">
                                {selectedDayPlaybacks.length > 0 && currentPlayback ? (
                                    <div className="space-y-4">
                                        {currentPlayback.isLocal || currentPlayback.url.endsWith('.mp4') || currentPlayback.url.includes('uploads') ? (
                                            <div className="relative aspect-video bg-black rounded-2xl overflow-hidden shadow-soft border border-slate-200 max-w-2xl">
                                                <video
                                                    key={currentPlayback.url}
                                                    src={currentPlayback.url}
                                                    controls
                                                    autoPlay={false}
                                                    controlsList="nodownload"
                                                    onContextMenu={(e) => e.preventDefault()}
                                                    className="w-full h-full object-contain"
                                                >
                                                    Your browser does not support the video tag.
                                                </video>
                                            </div>
                                        ) : (
                                            <div className="max-w-2xl bg-slate-50 border border-border rounded-2xl p-6 flex flex-col items-center text-center gap-4">
                                                <div className="p-3 bg-purple-50 text-purple-600 rounded-full border border-purple-100">
                                                    <ExternalLink size={24} />
                                                </div>
                                                <div className="space-y-1">
                                                    <h4 className="font-bold text-navy text-sm">External Link Recording</h4>
                                                    <p className="text-slate-body text-xs max-w-sm">This recording is hosted externally. Please click the button below to view it.</p>
                                                </div>
                                                <a
                                                    href={currentPlayback.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/95 text-white rounded-full text-xs font-bold shadow-md transition-all cursor-pointer"
                                                >
                                                    <PlayCircle size={14} />
                                                    Open Recording Link
                                                </a>
                                            </div>
                                        )}

                                        {/* Playlists grid if there are multiple recordings */}
                                        {selectedDayPlaybacks.length > 1 && (
                                            <div className="space-y-2.5 max-w-2xl pt-2">
                                                <span className="text-[10px] uppercase font-extrabold tracking-wider text-slate-400 block">
                                                    All Session Recordings ({selectedDayPlaybacks.length})
                                                </span>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                    {selectedDayPlaybacks.map((pb, pbIdx) => {
                                                        const isCurrent = currentPlayback.url === pb.url;
                                                        return (
                                                            <button
                                                                key={pbIdx}
                                                                type="button"
                                                                onClick={() => setCurrentPlayback(pb)}
                                                                className={`w-full text-left p-3 border rounded-xl flex items-center justify-between gap-3 text-xs font-bold transition-all cursor-pointer ${
                                                                    isCurrent
                                                                        ? 'bg-purple-600 border-purple-600 text-white shadow-sm font-extrabold'
                                                                        : 'bg-purple-50/50 hover:bg-purple-100/70 border-purple-100 text-purple-800'
                                                                }`}
                                                            >
                                                                <span className="flex items-center gap-2 truncate">
                                                                    <PlayCircle size={14} className={`shrink-0 ${isCurrent ? 'text-white' : 'text-purple-600'}`} />
                                                                    {pb.name}
                                                                </span>
                                                                <span className={`text-[9px] px-1.5 py-0.5 rounded shrink-0 font-bold uppercase tracking-wider ${
                                                                    isCurrent ? 'bg-white/20 text-white' : 'bg-purple-200/50 text-purple-900'
                                                                }`}>
                                                                    {isCurrent ? 'Playing' : pb.isLocal ? 'Play' : 'Open'}
                                                                </span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="max-w-2xl bg-slate-50 border border-border rounded-2xl p-5 flex flex-col items-center text-center gap-4">
                                        <div className="p-3 bg-blue-bg text-primary rounded-full">
                                            <Video size={28} />
                                        </div>
                                        <div className="space-y-1">
                                            <h4 className="font-bold text-navy text-sm">Class Recording Not Available</h4>
                                            <p className="text-slate-body text-xs max-w-sm">No recorded video has been uploaded for this day yet. You can join the live class using the button below if it is active.</p>
                                        </div>

                                        {/* Join class link button */}
                                        {selectedDay.is_mentor_session ? (
                                            hasLink ? (
                                                selectedDay.is_link_active ? (
                                                    <a
                                                        href={resolvedLink}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onPointerDown={ripple.onPointerDown}
                                                        className="ripple-trigger inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full text-xs font-bold shadow-md transition-all cursor-pointer"
                                                    >
                                                        <PlayCircle size={14} />
                                                        Join Mentor Session
                                                    </a>
                                                ) : (
                                                    new Date(selectedDay.date).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0) ? (
                                                        <div className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-gray-100 text-gray-400 rounded-full text-xs font-bold select-none cursor-not-allowed border border-gray-200">
                                                            <Clock size={14} />
                                                            Mentor Session link expired
                                                        </div>
                                                    ) : (
                                                        <div className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-indigo-50 text-indigo-500 rounded-full text-xs font-bold select-none cursor-not-allowed border border-indigo-100">
                                                            <Clock size={14} />
                                                            Mentor link activates 15m before session
                                                        </div>
                                                    )
                                                )
                                            ) : (
                                                <div className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-gray-100 text-gray-400 border border-gray-200 rounded-full text-xs font-bold select-none cursor-not-allowed">
                                                    <Clock size={14} />
                                                    Mentor session link not set yet
                                                </div>
                                            )
                                        ) : selectedDay.link ? (
                                            selectedDay.is_link_active ? (
                                                <a
                                                    href={selectedDay.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onPointerDown={ripple.onPointerDown}
                                                    className="ripple-trigger inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/95 text-white rounded-full text-xs font-bold shadow-md transition-all cursor-pointer"
                                                >
                                                    <PlayCircle size={14} />
                                                    Join Live Class
                                                </a>
                                            ) : (
                                                new Date(selectedDay.date).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0) ? (
                                                    <div className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-gray-100 text-gray-400 rounded-full text-xs font-bold select-none cursor-not-allowed border border-gray-200">
                                                        <Clock size={14} />
                                                        Class link expired (Class is over)
                                                    </div>
                                                ) : (
                                                    <div className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-gray-200 text-gray-500 rounded-full text-xs font-bold select-none cursor-not-allowed">
                                                        <Clock size={14} />
                                                        Class link activates 15m before class
                                                    </div>
                                                )
                                            )
                                        ) : (
                                            <span className="text-xs text-slate-400 font-medium">No live class link set for this day.</span>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Description Block (Only shown if recording is not available) */}
                            {selectedDay.is_unlocked && resolvedDescription && selectedDayPlaybacks.length === 0 && (
                                <div className="space-y-2 shrink-0">
                                    <span className="text-[10px] uppercase font-extrabold tracking-wider text-slate-400 block">
                                        Description
                                    </span>
                                    <p className="text-xs text-slate-body whitespace-pre-wrap leading-relaxed max-w-2xl bg-white border border-border rounded-xl p-4 shadow-2xs">
                                        {resolvedDescription}
                                    </p>
                                </div>
                            )}

                            {/* Resources (Attachments) Block */}
                            {selectedDay.is_unlocked && selectedDay.attachments && selectedDay.attachments.length > 0 && (
                                <div className="space-y-2 shrink-0">
                                    <span className="text-[10px] uppercase font-extrabold tracking-wider text-slate-400 block">
                                        Class Resources ({selectedDay.attachments.length})
                                    </span>
                                    <div className="flex flex-col gap-2 max-w-2xl bg-slate-50/50 border border-border rounded-xl p-4">
                                        {selectedDay.attachments.map((attachment: string, idx: number) => {
                                            let parsed = { filePath: attachment, fileName: attachment, fileSize: 'N/A' };
                                            try {
                                                if (attachment.startsWith('{') && attachment.endsWith('}')) {
                                                    parsed = JSON.parse(attachment);
                                                } else {
                                                    parsed.fileName = attachment.split('/').pop()?.replace(/^\d+-/, '') || attachment;
                                                }
                                            } catch {}
                                            return (
                                                <a
                                                    key={idx}
                                                    href={getFileUrl(parsed.filePath)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center justify-between p-2.5 hover:bg-white rounded-lg border border-transparent hover:border-border transition-all text-xs font-bold text-primary group"
                                                >
                                                    <span className="flex items-center gap-2 truncate">
                                                        <FileText size={14} className="text-slate-400" />
                                                        {parsed.fileName}
                                                    </span>
                                                    <span className="text-slate-400 text-[10px] font-medium shrink-0 group-hover:text-primary">
                                                        Download ({parsed.fileSize})
                                                    </span>
                                                </a>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                        );
                    })()}
                </div>
            </div>
        </div>
    );
};

export default StudentRecordings;
